import React, { useState, useEffect } from "react";
import { colocService, ColocDetails, getSelectedColoc } from "../services/coloc.service";
import { chargeCalculationsService } from "../services/charge-calculations.service";
import { roomsService } from "../services/rooms.service";
import type { RoomDetail } from "../types/room";
import PhotoManager from "../components/PhotoManager";
import AddressPicker from "../components/AddressPicker";
import FieldPreview from "../components/FieldPreview";
import { urlSitePublic } from "../utils/publicUrl";
import "../styles/ColocDetailsPage.css";

const ColocDetailsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [colocDetails, setColocDetails] = useState<ColocDetails | null>(null);
  const [hasChargeCalculations, setHasChargeCalculations] = useState<boolean>(false);
  // Somme des tantièmes des chambres. En copropriété, la totalité est
  // répartie entre les lots : il n'existe pas de tantièmes non attribués.
  // Le « total » n'est donc pas une donnée à saisir mais un résultat — le
  // saisir à part ouvrait la porte à un total ≠ somme, et donc à une
  // répartition fausse que rien ne signalait.
  const [tantiemesChambres, setTantiemesChambres] = useState<number | null>(null);
  // Deux onglets plutôt qu'une page continue : les champs attendent le
  // bouton, les photos partent dès qu'on les dépose. Les juxtaposer laissait
  // croire que le bouton enregistrait aussi les photos.
  const [onglet, setOnglet] = useState<'infos' | 'photos'>(
    () => (new URLSearchParams(window.location.search).get('onglet') === 'photos'
      ? 'photos'
      : 'infos'),
  );
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Auto-dismiss success messages after 4s (errors stay until user acts)
  useEffect(() => {
    if (message?.type !== "success") return;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    const loadColocDetails = async () => {
      setIsLoading(true);
      try {
        const selectedColoc = getSelectedColoc();
        if (selectedColoc) {
          const [details, calculations, chambres] = await Promise.all([
            colocService.getColocDetails(selectedColoc.id),
            chargeCalculationsService.listByColoc(selectedColoc.id).catch(() => []),
            roomsService.listForColoc(selectedColoc.id).catch(() => [] as RoomDetail[]),
          ]);
          setColocDetails(details);
          setHasChargeCalculations(calculations.length > 0);
          setTantiemesChambres(
            chambres.reduce((somme: number, c: RoomDetail) => somme + (c.tantiemes ?? 0), 0),
          );
        }
      } catch (error) {
        setMessage({ text: "Erreur lors du chargement des détails", type: "error" });
      } finally {
        setIsLoading(false);
      }
    };

    loadColocDetails();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!colocDetails) return;

    const { name, value, type } = e.target as HTMLInputElement;
    
    // Handle different input types
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setColocDetails({
        ...colocDetails,
        [name]: checked,
      });
    } else if (type === "number") {
      setColocDetails({
        ...colocDetails,
        [name]: value === "" ? null : Number(value),
      });
    } else {
      setColocDetails({
        ...colocDetails,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!colocDetails) return;

    setIsSaving(true);
    setMessage(null);

    try {
      const { id, slug, pictures, dateCreated, dateModified, ...editableFields } = colocDetails;
      await colocService.updateColocDetails(id, editableFields);
      setMessage({ text: "Détails de la colocation mis à jour avec succès", type: "success" });
    } catch (error) {
      setMessage({ text: "Erreur lors de la mise à jour des détails", type: "error" });
    } finally {
      setIsSaving(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (isLoading) {
    return <div className="loading">Chargement des détails...</div>;
  }

  if (!colocDetails) {
    return <div className="error-message">Aucune colocation sélectionnée</div>;
  }

  return (
    <div className="coloc-details-page">
      <h1>Détails de la colocation</h1>

      <div className="onglets" role="tablist">
        <button type="button" role="tab" aria-selected={onglet === 'infos'}
          className={onglet === 'infos' ? 'onglet actif' : 'onglet'}
          onClick={() => setOnglet('infos')}>
          Informations
        </button>
        <button type="button" role="tab" aria-selected={onglet === 'photos'}
          className={onglet === 'photos' ? 'onglet actif' : 'onglet'}
          onClick={() => setOnglet('photos')}>
          Photos
        </button>
      </div>
      
      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
      
      {onglet === 'infos' && (
      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <section className="form-section">
          <h2>Informations générales<FieldPreview apercu="presentation.png" titre="En-tête de votre site" description="Le nom sert de titre à la page, suivi du texte d'introduction puis du bandeau surface / chambres / salles de bain / cuisines." /></h2>
          <div className="form-group">
            <label htmlFor="name">Nom de la colocation *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={colocDetails.name}
              onChange={handleInputChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="slug">URL publique</label>
            <div className="url-display">
              <a
                href={urlSitePublic(colocDetails.slug)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {urlSitePublic(colocDetails.slug)}
              </a>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="introTxt">Texte d'introduction<FieldPreview apercu="presentation.png" titre="Premier paragraphe de la page" description="Deux ou trois phrases qui situent la colocation : le quartier, ce qui la distingue, à qui elle s'adresse." /></label>
            <textarea
              id="introTxt"
              name="introTxt"
              value={colocDetails.introTxt}
              onChange={handleInputChange}
              rows={4}
            />
          </div>
        </section>
        
        {/* Location */}
        {/* Localisation : adresse structuree + position sur la carte.
            « Description de la localisation » a ete deplacee vers la section
            « A proximite » — c'est la qu'elle s'affiche sur le site public,
            et le back-office doit refleter la page qu'il produit. */}
        <section className="form-section">
          <h2>Localisation<FieldPreview apercu="carte.png" titre="Bloc « Localisation »" description="Seul un cercle de 100 mètres autour de la position est affiché. L'adresse postale exacte n'apparaît jamais sur le site." /></h2>
          <AddressPicker
            value={{
              address: colocDetails.address ?? '',
              zipCode: colocDetails.zipCode ?? '',
              city: colocDetails.city ?? '',
              countryCode: colocDetails.countryCode ?? 'FR',
              location: colocDetails.location ?? null,
            }}
            onChange={(a) =>
              setColocDetails({
                ...colocDetails,
                address: a.address,
                zipCode: a.zipCode,
                city: a.city,
                countryCode: a.countryCode,
                location: a.location,
              })
            }
          />
        </section>
        
        {/* Features */}
        <section className="form-section">
          <h2>Caractéristiques<FieldPreview apercu="caracteristiques.png" titre="« À propos de cette colocation »" description="La description s'affiche sous ce titre, suivie du tableau des chiffres saisis ici : surface, étages, salles de bain, cuisines…" /></h2>
          <div className="form-group">
            <label htmlFor="featuresTxt">Description des caractéristiques<FieldPreview apercu="caracteristiques.png" titre="Corps de « À propos de cette colocation »" description="Le texte long qui décrit le logement : agencement, ambiance, parties communes." /></label>
            <textarea
              id="featuresTxt"
              name="featuresTxt"
              value={colocDetails.featuresTxt}
              onChange={handleInputChange}
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="amenitiesTxt">Description des équipements<FieldPreview apercu="equipements.png" titre="Texte du bloc « Services »" description="Ce que les cases à cocher ne disent pas : électroménager, mobilier fourni, services compris." /></label>
            <textarea
              id="amenitiesTxt"
              name="amenitiesTxt"
              value={colocDetails.amenitiesTxt}
              onChange={handleInputChange}
              rows={3}
            />
            {/* Cohérence : avertir si le texte libre annonce un équipement décoché dans les flags */}
            {(() => {
              const txt = (colocDetails.amenitiesTxt ?? "").toLowerCase();
              const mismatches: string[] = [];
              if (!colocDetails.isWifi && /\bwifi\b/.test(txt)) mismatches.push("Wifi");
              if (!colocDetails.isCleaningServices && /entretien|m[eé]nage|nettoyage/.test(txt))
                mismatches.push("Entretien");
              if (!colocDetails.isConcierge && /concierge|conciergerie/.test(txt))
                mismatches.push("Conciergerie");
              if (mismatches.length === 0) return null;
              return (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 12px",
                    background: "#fff4e5",
                    border: "1px solid #ffb74d",
                    borderRadius: 4,
                    color: "#a55a00",
                    fontSize: 14,
                  }}
                >
                  ⚠️ Incohérence détectée : le texte mentionne <strong>{mismatches.join(", ")}</strong> mais la case correspondante n'est pas cochée plus bas. Le site public affichera les deux informations contradictoires.
                </div>
              );
            })()}
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="surface">Surface (m²) *</label>
              <input
                type="number"
                id="surface"
                name="surface"
                value={colocDetails.surface ?? ""}
                onChange={handleInputChange}
                min={1}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="nbFloors">Nombre d'étages *</label>
              <input
                type="number"
                id="nbFloors"
                name="nbFloors"
                value={colocDetails.nbFloors ?? ""}
                onChange={handleInputChange}
                min={0}
               
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nbBathrooms">Salles de bain *</label>
              <input
                type="number"
                id="nbBathrooms"
                name="nbBathrooms"
                value={colocDetails.nbBathrooms ?? ""}
                onChange={handleInputChange}
                min={0}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="nbRestrooms">Toilettes *</label>
              <input
                type="number"
                id="nbRestrooms"
                name="nbRestrooms"
                value={colocDetails.nbRestrooms ?? ""}
                onChange={handleInputChange}
                min={0}
               
              />
            </div>

            <div className="form-group">
              <label htmlFor="nbKitchens">Cuisines *</label>
              <input
                type="number"
                id="nbKitchens"
                name="nbKitchens"
                value={colocDetails.nbKitchens ?? ""}
                onChange={handleInputChange}
                min={0}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="nbParkings">Places de parking *</label>
              <input
                type="number"
                id="nbParkings"
                name="nbParkings"
                value={colocDetails.nbParkings ?? ""}
                onChange={handleInputChange}
                min={0}
               
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="constructionYear">Année de construction</label>
              <input
                type="number"
                id="constructionYear"
                name="constructionYear"
                value={colocDetails.constructionYear ?? ""}
                onChange={handleInputChange}
                min={1800}
               
                max={new Date().getFullYear()}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="renovationYear">Année de rénovation</label>
              <input
                type="number"
                id="renovationYear"
                name="renovationYear"
                value={colocDetails.renovationYear ?? ""}
                onChange={handleInputChange}
                min={1800}
                max={new Date().getFullYear()}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <span className="form-readonly-label">Tantièmes totaux</span>
              <output className="form-readonly-value">
                {tantiemesChambres === null ? "…" : tantiemesChambres}
              </output>
              <small className="form-hint">
                Calculé : somme des tantièmes de vos chambres. C&apos;est le
                dénominateur de la répartition des charges — il se met à jour
                depuis l&apos;écran Chambres, il n&apos;y a rien à saisir ici.
              </small>
            </div>
          </div>
        </section>

        {/* Amenities */}
        <section className="form-section">
          <h2>Équipements et services<FieldPreview apercu="equipements.png" titre="Bloc « Services »" description="La description et les cases cochées ici forment ce bloc. Il disparaît du site si tout est vide." /></h2>
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isWifi"
                name="isWifi"
                checked={colocDetails.isWifi}
                onChange={handleInputChange}
              />
              <label htmlFor="isWifi">WiFi</label>
            </div>
            
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isCleaningServices"
                name="isCleaningServices"
                checked={colocDetails.isCleaningServices}
                onChange={handleInputChange}
              />
              <label htmlFor="isCleaningServices">Service de nettoyage</label>
            </div>
            
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isConcierge"
                name="isConcierge"
                checked={colocDetails.isConcierge}
                onChange={handleInputChange}
              />
              <label htmlFor="isConcierge">Conciergerie</label>
            </div>
            
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isGarden"
                name="isGarden"
                checked={colocDetails.isGarden}
                onChange={handleInputChange}
              />
              <label htmlFor="isGarden">Jardin</label>
            </div>
            
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isPetAllowed"
                name="isPetAllowed"
                checked={colocDetails.isPetAllowed}
                onChange={handleInputChange}
              />
              <label htmlFor="isPetAllowed">Animaux autorisés</label>
            </div>
            
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isElevator"
                name="isElevator"
                checked={colocDetails.isElevator}
                onChange={handleInputChange}
              />
              <label htmlFor="isElevator">Ascenseur</label>
            </div>
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isWashingMachine"
                name="isWashingMachine"
                checked={colocDetails.isWashingMachine}
                onChange={handleInputChange}
              />
              <label htmlFor="isWashingMachine">Lave-linge</label>
            </div>
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isDryer"
                name="isDryer"
                checked={colocDetails.isDryer}
                onChange={handleInputChange}
              />
              <label htmlFor="isDryer">Sèche-linge</label>
            </div>
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isDishwasher"
                name="isDishwasher"
                checked={colocDetails.isDishwasher}
                onChange={handleInputChange}
              />
              <label htmlFor="isDishwasher">Lave-vaisselle</label>
            </div>
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isBalcony"
                name="isBalcony"
                checked={colocDetails.isBalcony}
                onChange={handleInputChange}
              />
              <label htmlFor="isBalcony">Balcon ou terrasse</label>
            </div>
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isStorage"
                name="isStorage"
                checked={colocDetails.isStorage}
                onChange={handleInputChange}
              />
              <label htmlFor="isStorage">Cave ou local de rangement</label>
            </div>
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isBikeStorage"
                name="isBikeStorage"
                checked={colocDetails.isBikeStorage}
                onChange={handleInputChange}
              />
              <label htmlFor="isBikeStorage">Local à vélos</label>
            </div>
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isAirConditioning"
                name="isAirConditioning"
                checked={colocDetails.isAirConditioning}
                onChange={handleInputChange}
              />
              <label htmlFor="isAirConditioning">Climatisation</label>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="heatingType">Type de chauffage</label>
            <select
              id="heatingType"
              name="heatingType"
              value={colocDetails.heatingType}
              onChange={handleInputChange}
            >
              <option value={0}>Autre / Inconnu</option>
              <option value={1}>Électrique</option>
              <option value={2}>Gaz</option>
              <option value={3}>Fioul</option>
              <option value={4}>Bois</option>
              <option value={5}>Pompe à chaleur</option>
            </select>
          </div>
        </section>

        {/* Réglementation */}
        <section className="form-section">
          <h2>Réglementation</h2>
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isRentControlled"
                name="isRentControlled"
                checked={colocDetails.isRentControlled}
                onChange={handleInputChange}
              />
              <label htmlFor="isRentControlled">
                Zone encadrée (encadrement des loyers)
              </label>
            </div>
          </div>
          <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "4px" }}>
            Si activé, un champ « complément de loyer » apparaît sur chaque chambre
            et le site public affiche le loyer de base et le complément distinctement.
          </p>
          {colocDetails.isRentControlled && (
            <div className="form-group" style={{ marginTop: "12px", maxWidth: "320px" }}>
              <label htmlFor="rentReferenceMajored">
                Loyer de référence majoré (€/m²)
              </label>
              <input
                type="number"
                step="0.01"
                id="rentReferenceMajored"
                name="rentReferenceMajored"
                value={colocDetails.rentReferenceMajored ?? ""}
                onChange={handleInputChange}
              />
              <small style={{ color: "#666", display: "block", marginTop: "4px" }}>
                Plafond fixé par l&apos;arrêté préfectoral. Affiché dans l&apos;annonce.
              </small>
            </div>
          )}
        </section>

        {/* Charges */}
        <section className="form-section">
          <h2>Charges incluses<FieldPreview apercu="charges.png" titre="Bloc « Charges »" description="Chaque case cochée devient une ligne. Le bloc est masqué si aucune charge n'est incluse." /></h2>
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isChargesElectricity"
                name="isChargesElectricity"
                checked={colocDetails.isChargesElectricity}
                onChange={handleInputChange}
              />
              <label htmlFor="isChargesElectricity">Électricité</label>
            </div>
            
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isChargesWater"
                name="isChargesWater"
                checked={colocDetails.isChargesWater}
                onChange={handleInputChange}
              />
              <label htmlFor="isChargesWater">Eau</label>
            </div>
            
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isChargesHotWater"
                name="isChargesHotWater"
                checked={colocDetails.isChargesHotWater}
                onChange={handleInputChange}
              />
              <label htmlFor="isChargesHotWater">Eau chaude</label>
            </div>
            
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="isChargesHeating"
                name="isChargesHeating"
                checked={colocDetails.isChargesHeating}
                onChange={handleInputChange}
              />
              <label htmlFor="isChargesHeating">Chauffage</label>
            </div>
          </div>
        </section>

        {/* A proximite — reprend le titre EXACT de la section publique, et
            regroupe tout ce qui l'alimente : le texte de presentation du
            quartier puis les points d'interet. */}
        <section className="form-section">
          <h2>À proximité<FieldPreview apercu="quartier.png" titre="Bloc « À proximité »" description="La description du quartier ouvre le bloc, la liste des points d'intérêt la suit avec leurs distances." /></h2>
          <p className="form-help" style={{ color: "#666", marginBottom: "12px" }}>
            Ce bloc s&apos;affiche sur votre site public sous le titre «&nbsp;À proximité&nbsp;».
            Il est masqué si le texte et la liste sont vides.
          </p>
          <div className="form-group">
            <label htmlFor="locationTxt">Description du quartier<FieldPreview apercu="quartier.png" titre="Texte du bloc « À proximité »" description="Transports, commerces, campus : ce qui rend l'emplacement pratique au quotidien." /></label>
            <textarea
              id="locationTxt"
              name="locationTxt"
              value={colocDetails.locationTxt}
              onChange={handleInputChange}
              rows={3}
            />
          </div>
          <p className="form-help" style={{ color: "#666", marginBottom: "12px" }}>
            Points d&apos;intérêt — format : <code>Nom#distanceEnMètres</code> (ex&nbsp;: <code>Station Métro#200</code>)
          </p>
          {(colocDetails.nearbyPOIs ?? []).map((poi, index) => {
            const [poiName, poiDist] = poi.split("#");
            return (
              <div key={index} className="form-row" style={{ alignItems: "center", marginBottom: "8px" }}>
                <div className="form-group" style={{ flex: 2 }}>
                  <input
                    type="text"
                    placeholder="Nom du lieu"
                    value={poiName ?? ""}
                    onChange={(e) => {
                      const updated = [...(colocDetails.nearbyPOIs ?? [])];
                      updated[index] = `${e.target.value}#${poiDist ?? ""}`;
                      setColocDetails({ ...colocDetails, nearbyPOIs: updated });
                    }}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <input
                    type="number"
                    placeholder="Distance (m)"
                    value={poiDist ?? ""}
                    min={0}
                    onChange={(e) => {
                      const updated = [...(colocDetails.nearbyPOIs ?? [])];
                      updated[index] = `${poiName ?? ""}#${e.target.value}`;
                      setColocDetails({ ...colocDetails, nearbyPOIs: updated });
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const updated = (colocDetails.nearbyPOIs ?? []).filter((_, i) => i !== index);
                    setColocDetails({ ...colocDetails, nearbyPOIs: updated });
                  }}
                  style={{ marginLeft: "8px", padding: "6px 12px", cursor: "pointer" }}
                >
                  Supprimer
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => {
              const updated = [...(colocDetails.nearbyPOIs ?? []), "#"];
              setColocDetails({ ...colocDetails, nearbyPOIs: updated });
            }}
            style={{ marginTop: "8px", padding: "6px 14px", cursor: "pointer" }}
          >
            + Ajouter un point d'intérêt
          </button>
        </section>

        {/* Calculation Mode */}
        <section className="form-section">
          <h2>Calcul de charges</h2>
          <div className="form-group">
            <label htmlFor="calculationMode">Mode de calcul des charges</label>
            <select
              id="calculationMode"
              name="calculationMode"
              value={colocDetails.calculationMode}
              onChange={handleInputChange}
              disabled={hasChargeCalculations}
              className={hasChargeCalculations ? "read-only-field" : ""}
              title={hasChargeCalculations ? "Le mode de calcul ne peut plus être modifié car des calculs de charges existent déjà" : ""}
            >
              <option value="invoice_date">Par date de facture (méthode comptable)</option>
              <option value="prorata">Prorata temporis (par période de consommation)</option>
            </select>
            <small className="form-help" style={{ display: "block", marginTop: "4px", color: "#666" }}>
              {hasChargeCalculations
                ? "Le mode de calcul ne peut plus être modifié car des calculs de charges existent déjà."
                : colocDetails.calculationMode === "invoice_date"
                  ? "Chaque facture est imputée en totalité sur l'année de sa date de facturation."
                  : "Chaque facture est proratisée selon le chevauchement de sa période de consommation avec l'année."}
            </small>
          </div>
        </section>

        {/* Décision de Julien du 2026-10-11 : le serveur refuse aussi les montants
            au locataire, l'écran ne fait pas que les cacher. */}
        <section className="form-section">
          <h2>Espace locataire</h2>
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="showOnlyConsumption"
                name="showOnlyConsumption"
                checked={colocDetails.showOnlyConsumption}
                onChange={handleInputChange}
              />
              <label htmlFor="showOnlyConsumption">
                Montrer seulement les consommations, sans aucun montant
              </label>
            </div>
          </div>
          <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "4px" }}>
            Si activé, vos locataires ne voient dans leur espace que leurs consommations
            d&apos;énergie, en kWh et en m³ : ni le graphique des dépenses, ni les montants
            en euros. Leur loyer et leur provision pour charges restent affichés dans
            « Mon dossier ».
          </p>
        </section>

        {/* Épinglé en bas de l'écran : neuf sections séparent le haut du
            formulaire de son bouton, et il fallait remonter pour enregistrer.
            Rendu possible par les onglets — il ne surplombe jamais les photos,
            qui vivent ailleurs et s'enregistrent seules. */}
        <div className="form-actions form-actions--collante">
          <button type="submit" className="save-button" disabled={isSaving}>
            {isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
          </button>
        </div>
      </form>
      )}

      {onglet === 'photos' && (
      <section className="form-section">
        <h2>Photos<FieldPreview apercu="photos.png" titre="Galerie, en haut de page" description="La première photo occupe la vignette principale ; les autres alimentent la visionneuse « Voir les N photos »." /></h2>
        {/* Dit explicitement ce que la mise en page laissait deviner de travers :
            ce bloc est hors du formulaire, chaque photo part au moment où on la
            dépose. Sans cette phrase, le bouton juste au-dessus donne à penser
            qu'il faut l'actionner pour que les photos soient conservées. */}
        <p className="form-hint">
          Les photos sont enregistrées dès leur ajout : il n&apos;y a rien à
          valider ici.
        </p>
        <PhotoManager entityType="coloc" entityId={colocDetails.id} />
      </section>
      )}
    </div>
  );
};

export default ColocDetailsPage;
